import type { PointerLikeEvent } from '../../../types/browserEvents';
import { isExtension } from '../../../utils/environment';
import makeLogger from '../../../utils/makeLogger';
import areRectsEqual from '../../utils/areRectsEqual';
import classListSafe from '../../utils/classListSafe';
import deleteCssHighlight from '../../utils/deleteCssHighlight';
import getClosestHTMLElement from '../../utils/getClosestHTMLElement';
import makeIntoNonOverlappingRects from '../../utils/makeIntoNonOverlappingRects';
import Renderer from '../../utils/Renderer';
import * as autoScroller from '../autoScroller';
// eslint-disable-next-line import/no-cycle
import { getRendererOrThrow } from '../index';

/*
  NOTE: this directory is intended to be called from within the content frame, not outside
*/

declare let window: WindowWithAPIsMissingFromTypeScript;

/*
  - domElements (default): Absolutely-positioned DOM elements are drawn over the selection's rects'
    positions. Limitation: it adds a background colour (on top) but we can't change the text colour (at
    least right now). Note: Native text selections' text colour changes in some situations.
  - cssHighlights: Uses the CSS Highlights API. This is less battle-tested for selection emulation.
    Limitations: We can control the text colour but the Highlights API is limited. It only renders against
    text, not images for example. If we ever needed more styling, the allowed CSS rules are limited.
*/
type RenderingMode = 'cssHighlights' | 'domElements';

const logger = makeLogger(__filename);

// Paused means the currently rendered selection is kept as is but we stop reacting to events
let selectionEmulationStatus: 'active' | 'inactive' | 'paused' = 'inactive';
let renderingMode: RenderingMode = 'domElements';
const selectionEmulationContainerId = 'selection-emulation';
const selectionHighlightName = 'selection-emulation';

function clearRendered() {

  /*
    Clear both mode artifacts to be safe: each operation is a no-op if the
    artifact doesn't exist, and this avoids leaks if the rendering mode
    changes between sessions.
  */
  deleteCssHighlight(selectionHighlightName);
  document.getElementById(selectionEmulationContainerId)?.remove();
}

function drawRectsForRanges(ranges: Range[], containerNode: HTMLElement) {
  let selectionContainer = document.getElementById(selectionEmulationContainerId);
  if (!selectionContainer) {
    selectionContainer = document.createElement('div');
    selectionContainer.id = selectionEmulationContainerId;
    containerNode.appendChild(selectionContainer);
  }

  const containerRect = containerNode.getBoundingClientRect();

  // The rects we want to draw
  const selectionRects = makeIntoNonOverlappingRects(
    ranges.flatMap((range) => Array.from(range.getClientRects()))
      .filter((rect) => rect.width >= 1 || rect.height >= 1),
  );

  // Preserve the end which isn't changing, to avoid unnecessary flickering
  let indexOfFirstSelectionContainerChildToKeep = 0;
  let indexOfLastSelectionContainerChildToKeep = selectionRects.length - 1;
  const previousDrawnRects = Array.from(selectionContainer.children, (child) =>
    child.getBoundingClientRect());
  if (
    previousDrawnRects &&
    areRectsEqual(
      previousDrawnRects[previousDrawnRects.length - 1],
      selectionRects[selectionRects.length - 1],
    )
  ) {
    indexOfLastSelectionContainerChildToKeep = previousDrawnRects.length - 1;
    indexOfFirstSelectionContainerChildToKeep = Math.max(
      0,
      indexOfLastSelectionContainerChildToKeep - selectionRects.length + 1,
    );
  }

  // Remove children from last time that we don't need anymore
  Array.from(selectionContainer.children).forEach((child, index) => {
    if (
      index < indexOfFirstSelectionContainerChildToKeep ||
      index > indexOfLastSelectionContainerChildToKeep
    ) {
      child.remove();
    }
  });

  // Draw rects with elements
  for (let i = 0; i < selectionRects.length; i++) {
    const rect = selectionRects[i];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    let selectionContainerChild = selectionContainer.children[i] as HTMLElement; // claude eslint: selectionContainer only ever contains span elements appended below; children[i] is always an HTMLElement
    if (!selectionContainerChild) {
      selectionContainerChild = document.createElement('span');
      selectionContainer.appendChild(selectionContainerChild);
    }

    for (const propertyName of ['top', 'right', 'bottom', 'left', 'height', 'width'] as const) {
      let value = rect[propertyName];
      if (['top', 'left', 'right', 'bottom'].includes(propertyName)) {
        value = value - containerRect[propertyName];
      }
      selectionContainerChild.style[propertyName] = `${value}px`;
    }
  }
}

export function onSelectionChange() {
  const selection = getSelection();
  logger.debug('onSelectionChange', {
    selectionEmulationStatus,
    selectionRange: selection?.rangeCount && selection.getRangeAt(0),
  });
  if (selectionEmulationStatus !== 'active') {
    return;
  }

  if (
    !selection?.rangeCount ||
    !selection
      .toString()
      .replace(/\u2060/g, '')
      .trim()
  ) {
    clearRendered();
    return;
  }

  const containerNode = getRendererOrThrow().containerNode;
  const ranges: Range[] = [];
  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);
    if (!range) {
      continue;
    }

    /*
      On iOS, if the edge being resized was on the handle, a big selection square was (later) drawn, so
      let's move the selection inside past the handle
    */
    const startContainerElement = getClosestHTMLElement(range.startContainer);
    if (startContainerElement?.closest(`.${Renderer.highlightResizeHandleClassName}`)) {
      range.setStartAfter(startContainerElement);
    }
    const endContainerElement = getClosestHTMLElement(range.endContainer);
    if (endContainerElement?.closest(`.${Renderer.highlightResizeHandleClassName}`)) {
      range.setEndBefore(endContainerElement);
    }

    if (!containerNode.contains(range.commonAncestorContainer)) {
      continue;
    }

    ranges.push(range);
  }

  if (ranges.length === 0) {
    clearRendered();
    return;
  }

  if (renderingMode === 'cssHighlights') {
    CSS.highlights.set(selectionHighlightName, new window.Highlight(...ranges));
  } else {
    drawRectsForRanges(ranges, containerNode);
  }
}

export function pauseEmulatingSelection() {
  logger.debug('pauseEmulatingSelection', { selectionEmulationStatus });
  if (selectionEmulationStatus !== 'active') {
    return;
  }
  autoScroller.stopListening();
  selectionEmulationStatus = 'paused';
}

export function startEmulatingSelection({
  lastPointerDownEvent,
  renderingMode: newRenderingMode = 'domElements',
  shouldAutoScroll = true,
}: {
  lastPointerDownEvent?: PointerLikeEvent | null;
  renderingMode?: RenderingMode;
  shouldAutoScroll?: boolean;
} = {}) {
  if (isExtension) {
    throw new Error("Selection emulation isn't supported in the extension (it's missing some styles)");
  }

  logger.debug('startEmulatingSelection', { lastPointerDownEvent, renderingMode: newRenderingMode, selectionEmulationStatus, shouldAutoScroll });
  if (selectionEmulationStatus === 'active') {
    return;
  }

  if (shouldAutoScroll && !lastPointerDownEvent?.target) {
    throw new Error('startEmulatingSelection: shouldAutoScroll requires lastPointerDownEvent');
  }

  selectionEmulationStatus = 'active';
  renderingMode = newRenderingMode;
  const containerNode = getRendererOrThrow().containerNode;
  classListSafe.add(containerNode, 'is-emulating-selection');
  onSelectionChange(); // Draw current selection
  document.addEventListener('selectionchange', onSelectionChange);
  if (shouldAutoScroll && lastPointerDownEvent) {
    autoScroller.startListening(lastPointerDownEvent);
  }
}

export function stopEmulatingSelection() {
  logger.debug('stopEmulatingSelection', { selectionEmulationStatus });
  if (selectionEmulationStatus === 'inactive') {
    return;
  }
  autoScroller.stopListening();
  selectionEmulationStatus = 'inactive';
  document.removeEventListener('selectionchange', onSelectionChange);
  classListSafe.remove(getRendererOrThrow().containerNode, 'is-emulating-selection');
  clearRendered();
}
